import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label, NativeSelect } from "@realroof/ui";

import { api, canSeeAudit } from "../api";
import { when } from "../format";
import { useSession } from "../session";
import { Table, td, th } from "../table";
import type { PackAdmin } from "./Plans";

interface Coupon {
  code: string;
  percent: number;
  pack_id: string | null;
  pack_name: string | null;
  valid_from: string | null;
  valid_until: string | null;
  max_uses: number | null;
  per_user: number;
  active: boolean;
  note: string | null;
  uses: number;
  created_at: string;
}

/** "2026-12-31" (local date) -> ISO at the end of that day in India. */
const endOfDay = (d: string) => (d ? new Date(`${d}T23:59:59+05:30`).toISOString() : null);
const startOfDay = (d: string) => (d ? new Date(`${d}T00:00:00+05:30`).toISOString() : null);

/** Coupons: a percentage off one pack or every pack. Only paid orders count as uses. */
export function Coupons() {
  const { me } = useSession();
  const canEdit = canSeeAudit(me?.staff_role); // admins and super admins
  const [rows, setRows] = useState<Coupon[] | null>(null);
  const [packs, setPacks] = useState<PackAdmin[]>([]);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("10");
  const [pack, setPack] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [perUser, setPerUser] = useState("1");
  const [note, setNote] = useState("");

  const load = () =>
    api<Coupon[]>("/admin/coupons")
      .then(setRows)
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  useEffect(() => {
    load();
    api<PackAdmin[]>("/admin/packs")
      .then(setPacks)
      .catch(() => setPacks([]));
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    try {
      const made = await api<Coupon>("/admin/coupons", {
        method: "POST",
        body: {
          code,
          percent: Number(percent),
          pack_id: pack || null,
          valid_from: startOfDay(from),
          valid_until: endOfDay(until),
          max_uses: maxUses ? Number(maxUses) : null,
          per_user: Number(perUser),
          note: note || null,
        },
      });
      setMessage({ tone: "success", text: `${made.code} created: ${made.percent}% off ${made.pack_name ?? "every pack"}.` });
      setCode("");
      setNote("");
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  async function toggle(c: Coupon) {
    const { code: id, pack_name: _p, uses: _u, created_at: _c, ...rest } = c;
    try {
      await api(`/admin/coupons/${id}`, { method: "PUT", body: { ...rest, active: !c.active } });
      setMessage({ tone: "success", text: `${id} ${c.active ? "switched off" : "switched on"}.` });
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Coupons</h1>
      <p className="text-sm text-muted-foreground">A percentage off one pack or every pack. GST is charged on the price after the discount; a 100% coupon needs no payment.</p>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {canEdit && (
        <Card>
          <CardTitle className="text-base">New coupon</CardTitle>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Label>
              Code
              <Input required pattern="[A-Za-z0-9]{3,20}" className="font-mono uppercase" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
            </Label>
            <Label>
              Percent off
              <Input required inputMode="numeric" value={percent} onChange={(e) => setPercent(e.target.value.replace(/\D/g, ""))} />
            </Label>
            <Label>
              For
              <NativeSelect value={pack} onChange={(e) => setPack(e.target.value)}>
                <option value="">Every pack</option>
                {packs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Label>
            <Label>
              Note (staff only)
              <Input maxLength={120} value={note} onChange={(e) => setNote(e.target.value)} />
            </Label>
            <Label>
              Valid from (optional)
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Label>
            <Label>
              Valid until (optional)
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            </Label>
            <Label>
              Total uses (optional)
              <Input inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/\D/g, ""))} />
            </Label>
            <Label>
              Uses per person
              <Input required inputMode="numeric" value={perUser} onChange={(e) => setPerUser(e.target.value.replace(/\D/g, ""))} />
            </Label>
            <Button type="submit" variant="primary" className="self-start">
              Create coupon
            </Button>
          </form>
        </Card>
      )}
      {rows?.length === 0 && <p className="text-muted-foreground">No coupons yet.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Code</th>
              <th className={th}>Discount</th>
              <th className={th}>Valid</th>
              <th className={th}>Used</th>
              <th className={th}>Status</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.code}>
                <td className={td}>
                  <span className="font-mono font-semibold">{c.code}</span>
                  {c.note && <div className="text-xs text-muted-foreground">{c.note}</div>}
                </td>
                <td className={td}>
                  {c.percent}% off {c.pack_name ?? "every pack"}
                </td>
                <td className={`${td} text-sm`}>
                  {c.valid_from ? `from ${when(c.valid_from)}` : "now"}
                  <br />
                  {c.valid_until ? `until ${when(c.valid_until)}` : "no end"}
                </td>
                <td className={td}>
                  {c.uses}
                  {c.max_uses ? ` / ${c.max_uses}` : ""} · {c.per_user} per person
                </td>
                <td className={td}>
                  <Badge tone={c.active ? "success" : "neutral"}>{c.active ? "On" : "Off"}</Badge>
                </td>
                <td className={td}>
                  {canEdit && (
                    <Button size="sm" onClick={() => toggle(c)}>
                      {c.active ? "Switch off" : "Switch on"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
